<template>
  <Teleport to="body">
    <div v-if="modelValue" class="fixed inset-0 z-[80] overflow-y-auto">
      <div class="fixed inset-0 bg-black/45" @click="close"></div>
      <div class="relative flex min-h-screen items-start justify-center p-3 sm:items-center sm:p-4">
        <div class="relative flex max-h-[calc(100vh-1.5rem)] w-full max-w-6xl flex-col overflow-hidden rounded-lg bg-white shadow-xl sm:max-h-[90vh]">
          <header class="border-b border-slate-200 px-4 py-4 sm:px-5">
            <div class="flex items-start justify-between gap-3">
              <div class="min-w-0">
                <h3 class="text-lg font-semibold text-slate-900">指定下游</h3>
                <p class="mt-1 text-sm text-slate-500">
                  {{ companyName }} · 指定金额是边金额，也是下游进项金额。
                </p>
              </div>
              <button type="button" class="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600" @click="close">
                <span class="sr-only">关闭</span>
                <XMarkIcon class="h-5 w-5" />
              </button>
            </div>
          </header>

          <main class="min-h-0 flex-1 overflow-y-auto p-4 sm:p-5">
            <div class="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <div class="rounded-md bg-slate-50 p-3">
                <div class="text-xs text-slate-500">目标销项</div>
                <div class="mt-1 text-base font-semibold text-slate-900">{{ formatInvoiceAmount(targetAmount) }}</div>
              </div>
              <div class="rounded-md bg-slate-50 p-3">
                <div class="text-xs text-slate-500">已指定</div>
                <div class="mt-1 text-base font-semibold" :class="overAllocated ? 'text-red-600' : 'text-slate-900'">{{ formatInvoiceAmount(totalAmount) }}</div>
              </div>
              <div class="rounded-md bg-slate-50 p-3">
                <div class="text-xs text-slate-500">剩余自动生成</div>
                <div class="mt-1 text-base font-semibold text-blue-700">{{ formatInvoiceAmount(remainingAmount) }}</div>
              </div>
              <div class="rounded-md bg-slate-50 p-3">
                <div class="text-xs text-slate-500">配置数</div>
                <div class="mt-1 text-base font-semibold text-slate-900">{{ enabledCount }} / {{ rows.length }}</div>
              </div>
            </div>

            <div class="overflow-hidden rounded-lg border border-slate-200">
              <div class="border-b border-slate-200 bg-white px-3 py-3">
                <label class="block">
                  <span class="sr-only">搜索下游客户</span>
                  <input
                    v-model="targetKeyword"
                    type="search"
                    class="form-search-input form-control-sm w-full"
                    placeholder="搜索公司、区域"
                  >
                </label>
              </div>
              <div ref="allocationTableScrollRef" class="overflow-x-auto">
                <table class="min-w-[1040px] w-full divide-y divide-slate-200 text-sm">
                  <thead class="bg-slate-50 text-left text-xs font-semibold text-slate-500">
                    <tr>
                      <th class="data-table-sticky-left data-table-sticky-head data-table-sticky-identity px-3 py-2.5">下游客户</th>
                      <th class="w-28 px-3 py-2.5">名单区域</th>
                      <th class="w-36 px-3 py-2.5 text-right">指定金额</th>
                      <th class="w-24 px-3 py-2.5 text-center">启用</th>
                      <th class="w-24 px-3 py-2.5 text-right">排序</th>
                      <th class="px-3 py-2.5">备注</th>
                      <th class="data-table-sticky-right data-table-sticky-head data-table-sticky-action w-16 px-3 py-2.5 text-right">操作</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-slate-100 bg-white">
                    <tr v-if="rows.length === 0">
                      <td colspan="7" class="px-3 py-8 text-center text-slate-500">暂无指定下游</td>
                    </tr>
                    <tr v-for="(row, index) in rows" :key="row.localId">
                      <td class="data-table-sticky-left data-table-sticky-identity px-3 py-2.5">
                        <div class="space-y-1.5">
                          <el-select
                            v-model="row.toPlanCustomerId"
                            class="form-select form-control-sm w-full"
                            filterable
                            :filter-method="filterTargetOptions"
                            popper-class="enterprise-invoice-downstream-select"
                            placeholder="选择下游客户"
                          >
                            <el-option
                              v-for="customer in filteredTargetOptions"
                              :key="customer.id"
                              :label="customer.label"
                              :value="customer.id"
                              :disabled="isTargetUsed(customer.id, index)"
                            >
                              <div class="downstream-option-row">
                                <span class="min-w-0 flex-1 truncate font-medium text-slate-800">{{ customer.label }}</span>
                                <span class="downstream-option-meta">{{ customer.regionName || customer.registeredRegionSnapshot || '未分组' }}</span>
                                <span class="downstream-option-meta">销 {{ formatInvoiceAmount(customer.targetOutputAmount) }}</span>
                                <span class="downstream-option-meta">进 {{ formatInvoiceAmount(customer.targetInputAmount) }}</span>
                                <span class="downstream-option-meta text-blue-600">余 {{ formatInvoiceAmount(targetInputAvailability(customer.id, index).remainingAmount) }}</span>
                                <span class="downstream-option-meta">{{ formatProfitRate(customer.targetProfitRate) }}</span>
                              </div>
                            </el-option>
                          </el-select>
                          <div v-if="selectedTarget(row.toPlanCustomerId)" class="grid grid-cols-2 gap-1 rounded-md bg-slate-50 px-2 py-1 text-[11px] leading-4 text-slate-500 md:grid-cols-4">
                            <span>目标销项 {{ formatInvoiceAmount(selectedTarget(row.toPlanCustomerId).targetOutputAmount) }}</span>
                            <span>目标进项 {{ formatInvoiceAmount(selectedTarget(row.toPlanCustomerId).targetInputAmount) }}</span>
                            <span :class="rowInputOverAvailable(row, index) ? 'font-medium text-red-600' : 'text-blue-600'">
                              剩余进项 {{ formatInvoiceAmount(targetInputAvailability(row.toPlanCustomerId, index).remainingAmount) }}
                            </span>
                            <span>利润率 {{ formatProfitRate(selectedTarget(row.toPlanCustomerId).targetProfitRate) }}</span>
                          </div>
                          <p v-if="rowInputOverAvailable(row, index)" class="text-[11px] leading-4 text-red-600">
                            指定金额超过该下游剩余可分配进项，保存时后端会拒绝。
                          </p>
                        </div>
                      </td>
                      <td class="px-3 py-2.5 text-xs text-slate-500">
                        {{ selectedTarget(row.toPlanCustomerId)?.regionName || selectedTarget(row.toPlanCustomerId)?.registeredRegionSnapshot || '-' }}
                      </td>
                      <td class="px-3 py-2.5">
                        <input
                          v-model="row.amount"
                          type="number"
                          min="0"
                          step="100"
                          class="form-input form-control-sm w-full text-right tabular-nums"
                          placeholder="0"
                        >
                      </td>
                      <td class="px-3 py-2.5 text-center">
                        <input v-model="row.enabled" type="checkbox" class="form-check">
                      </td>
                      <td class="px-3 py-2.5">
                        <input
                          v-model.number="row.displayOrder"
                          type="number"
                          min="1"
                          step="1"
                          class="form-input form-control-sm w-full text-right tabular-nums"
                        >
                      </td>
                      <td class="px-3 py-2.5">
                        <input v-model="row.remark" type="text" maxlength="255" class="form-input form-control-sm w-full" placeholder="可选">
                      </td>
                      <td class="data-table-sticky-right data-table-sticky-action px-3 py-2.5 text-right">
                        <button type="button" class="table-action-button is-danger" v-permission="'enterprise-invoice:plan-customer:manage'" title="删除" @click="removeRow(index)">
                          <TrashIcon class="h-3.5 w-3.5" />
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <button type="button" class="btn-secondary mt-3 inline-flex w-full items-center justify-center gap-1.5 sm:w-auto" v-permission="'enterprise-invoice:plan-customer:manage'" @click="addRow">
              <PlusIcon class="h-4 w-4" />
              新增下游
            </button>

            <p v-if="overAllocated" class="mt-3 text-sm text-red-600">已指定金额超过目标销项额度，请调整后再保存。</p>
          </main>

          <footer class="flex flex-col-reverse gap-3 border-t border-slate-200 px-4 py-4 sm:flex-row sm:justify-end sm:px-5">
            <button type="button" class="btn-secondary w-full sm:w-auto" :disabled="submitting" @click="close">取消</button>
            <button type="button" class="btn-primary w-full sm:w-auto" v-permission="'enterprise-invoice:plan-customer:manage'" :disabled="submitting" @click="submit">
              {{ submitting ? '保存中...' : '保存指定下游' }}
            </button>
          </footer>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { ElMessage } from 'element-plus'
import { PlusIcon, TrashIcon, XMarkIcon } from '@heroicons/vue/24/outline'
import { formatInvoiceAmount, formatProfitRate } from '../utils'
import { useWheelToHorizontalScroll } from '../composables/useWheelToHorizontalScroll'
import {
  allocationsForCustomer,
  downstreamDraftSummary,
  downstreamTargetInputAvailability,
  selectableDownstreamCustomers
} from '../downstreamRules.mjs'
import { displayInvoiceCompanyName } from '../matrixRules.mjs'

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  customer: { type: Object, default: null },
  customers: { type: Array, default: () => [] },
  allocations: { type: Array, default: () => [] },
  submitting: { type: Boolean, default: false }
})

const emit = defineEmits(['update:modelValue', 'submit'])

const rows = ref([])
const targetKeyword = ref('')
const allocationTableScrollRef = useWheelToHorizontalScroll()
let nextLocalId = 1

const companyName = computed(() => props.customer ? displayInvoiceCompanyName(props.customer) : '-')
const targetAmount = computed(() => Number(props.customer?.targetOutputAmount || 0))
const targetOptions = computed(() => selectableDownstreamCustomers(props.customers, props.customer?.id))
const filteredTargetOptions = computed(() => {
  const keyword = targetKeyword.value.trim().toLowerCase()
  if (!keyword) return targetOptions.value
  return targetOptions.value.filter(customer => {
    const text = [
      customer.label,
      customer.regionName,
      customer.registeredRegionSnapshot,
      customer.registeredRegion
    ].filter(Boolean).join(' ').toLowerCase()
    return text.includes(keyword)
  })
})
const summary = computed(() => downstreamDraftSummary(rows.value, props.customer))
const totalAmount = computed(() => summary.value.totalAmount)
const remainingAmount = computed(() => summary.value.remainingAmount)
const enabledCount = computed(() => summary.value.enabledCount)
const overAllocated = computed(() => summary.value.overAllocated)

function resetRows() {
  targetKeyword.value = ''
  const source = allocationsForCustomer(props.allocations, props.customer?.id)
  rows.value = source.map((item, index) => ({
    localId: nextLocalId++,
    toPlanCustomerId: item.toPlanCustomerId,
    amount: item.amount ?? null,
    enabled: item.enabled !== false,
    displayOrder: item.displayOrder || index + 1,
    remark: item.remark || ''
  }))
}

function addRow() {
  rows.value.push({
    localId: nextLocalId++,
    toPlanCustomerId: null,
    amount: null,
    enabled: true,
    displayOrder: rows.value.length + 1,
    remark: ''
  })
}

function removeRow(index) {
  rows.value.splice(index, 1)
}

function isTargetUsed(targetId, ignoreIndex) {
  return rows.value.some((row, index) =>
    index !== ignoreIndex &&
    row.toPlanCustomerId != null &&
    String(row.toPlanCustomerId) === String(targetId)
  )
}

function selectedTarget(targetId) {
  if (targetId === null || targetId === undefined || targetId === '') return null
  return targetOptions.value.find(customer => String(customer.id) === String(targetId)) || null
}

function targetInputAvailability(targetId, rowIndex) {
  return downstreamTargetInputAvailability({
    target: selectedTarget(targetId),
    savedAllocations: props.allocations,
    draftRows: rows.value,
    editingFromPlanCustomerId: props.customer?.id ?? props.customer?.planCustomerId,
    currentRowIndex: rowIndex
  })
}

function rowInputOverAvailable(row, rowIndex) {
  if (!row || row.enabled === false || !row.toPlanCustomerId) return false
  const amount = Number(row.amount)
  if (!Number.isFinite(amount) || amount <= 0) return false
  return amount > targetInputAvailability(row.toPlanCustomerId, rowIndex).remainingAmount
}

function filterTargetOptions(keyword = '') {
  targetKeyword.value = keyword
}

function close() {
  emit('update:modelValue', false)
}

function submit() {
  const normalized = []
  const seen = new Set()
  for (const [index, row] of rows.value.entries()) {
    if (!row.toPlanCustomerId) {
      ElMessage.warning(`第 ${index + 1} 行请选择下游客户`)
      return
    }
    if (seen.has(String(row.toPlanCustomerId))) {
      ElMessage.warning('下游客户不能重复')
      return
    }
    seen.add(String(row.toPlanCustomerId))
    const amount = Number(row.amount)
    if (!Number.isFinite(amount) || amount <= 0) {
      ElMessage.warning(`第 ${index + 1} 行金额必须大于 0`)
      return
    }
    normalized.push({
      toPlanCustomerId: row.toPlanCustomerId,
      amount,
      enabled: row.enabled !== false,
      displayOrder: Number(row.displayOrder || index + 1),
      remark: String(row.remark || '').trim() || null
    })
  }
  if (overAllocated.value) {
    ElMessage.warning('已指定金额不能超过目标销项额度')
    return
  }
  emit('submit', {
    customer: props.customer,
    allocations: normalized
  })
}

watch(() => props.modelValue, value => {
  if (value) resetRows()
})

watch(() => [props.customer?.id, props.allocations], () => {
  if (props.modelValue) resetRows()
}, { deep: true })
</script>

<style scoped>
.downstream-option-row {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 10px;
}

.downstream-option-meta {
  flex-shrink: 0;
  color: #64748b;
  font-size: 12px;
}
</style>

<style>
.enterprise-invoice-downstream-select .el-select-dropdown__item {
  height: 36px;
  line-height: 36px;
}

.enterprise-invoice-downstream-select .downstream-option-row {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 10px;
}

.enterprise-invoice-downstream-select .downstream-option-meta {
  flex-shrink: 0;
  color: #64748b;
  font-size: 12px;
}
</style>
